import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { inquiryApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ChatText, User, EnvelopeSimple, Phone, Calendar, Car, CheckCircle,
  XCircle, ArrowRight, Trash, Eye, Funnel, ArrowsClockwise, PaperPlaneRight,
  Paperclip, DownloadSimple, ArrowUUpLeft, Plus,
} from '@phosphor-icons/react';
import { emailTemplateApi } from '../services/api';
import { SaveAsTemplateButton } from '../components/EmailTemplatesManager';
import ManualInquiryDialog from '../components/ManualInquiryDialog';

const STATUS_BADGES = {
  new: { label: 'Neu', cls: 'badge-warning' },
  reviewed: { label: 'Gesichtet', cls: 'badge-info' },
  converted: { label: 'Umgewandelt', cls: 'badge-success' },
  rejected: { label: 'Abgelehnt', cls: 'badge-neutral' },
};

const Inquiries = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, reviewed: 0, converted: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  // Thread + reply
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState('');
  const [showManualDialog, setShowManualDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        inquiryApi.list(statusFilter !== 'all' ? { status: statusFilter } : {}),
        inquiryApi.stats(),
      ]);
      setInquiries(list.data || []);
      setStats(st.data || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const markStatus = async (id, status) => {
    try {
      await inquiryApi.update(id, { status });
      toast.success('Status aktualisiert');
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleConvert = async (inq) => {
    try {
      const res = await inquiryApi.convert(inq.id);
      toast.success('Kunde angelegt – Anfrage umgewandelt');
      load();
      setSelected(null);
      navigate(`/customers/${res.data.customer.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Anfrage wirklich löschen?')) return;
    try {
      await inquiryApi.delete(id);
      toast.success('Anfrage gelöscht');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleFetchEmails = async () => {
    setIsFetching(true);
    try {
      const res = await inquiryApi.fetchEmails();
      const { new_inquiries = 0, new_replies = 0, fetched = 0 } = res.data || {};
      if (fetched === 0) {
        toast.info('Posteingang ist aktuell — keine neuen Nachrichten.');
      } else {
        toast.success(`${fetched} neue E-Mail(s) • ${new_inquiries} neue Anfragen • ${new_replies} Antworten im Thread`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIsFetching(false);
    }
  };

  const loadMessages = async (inquiryId) => {
    setLoadingMessages(true);
    try {
      const res = await inquiryApi.getMessages(inquiryId);
      setMessages(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendReply = async () => {
    if (!selected || !replyBody.trim()) { toast.error('Bitte eine Antwort eingeben'); return; }
    setSendingReply(true);
    try {
      await inquiryApi.reply(selected.id, replyBody, replySubject || null, replyFiles);
      toast.success(`Antwort an ${selected.email} gesendet`);
      setReplyBody('');
      setReplySubject('');
      setReplyFiles([]);
      await loadMessages(selected.id);
      // Bump local status to reviewed
      if (selected.status === 'new') {
        setSelected({ ...selected, status: 'reviewed' });
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingReply(false);
    }
  };

  useEffect(() => {
    if (selected?.id) {
      loadMessages(selected.id);
      setReplyBody('');
      setReplySubject('');
      setReplyFiles([]);
      setSelectedTpl('');
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  // Load templates once
  useEffect(() => {
    emailTemplateApi.list().then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const applyTemplate = async (tplId) => {
    if (!tplId) return;
    setSelectedTpl(tplId);
    try {
      const res = await emailTemplateApi.render(tplId, selected?.id);
      const { subject, body } = res.data || {};
      if (subject) setReplySubject(subject);
      if (body) setReplyBody(body);
      toast.success('Vorlage übernommen');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111418]">Anfragen</h1>
          <p className="text-sm text-[#525866] mt-1">
            Homepage-Anfragen sowie E-Mails an dein Postfach.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowManualDialog(true)}
            variant="outline"
            data-testid="create-manual-inquiry-btn"
          >
            <Plus weight="bold" size={16} className="mr-2" />
            Anfrage erstellen
          </Button>
          <Button
            onClick={handleFetchEmails}
            disabled={isFetching}
            className="btn-primary"
            data-testid="fetch-emails-btn"
          >
            <ArrowsClockwise weight="bold" size={16} className={`mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {isFetching ? 'Rufe ab…' : 'Posteingang abrufen'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Gesamt', value: stats.total, key: 'all' },
          { label: 'Neu', value: stats.new, key: 'new' },
          { label: 'Gesichtet', value: stats.reviewed, key: 'reviewed' },
          { label: 'Umgewandelt', value: stats.converted, key: 'converted' },
          { label: 'Abgelehnt', value: stats.rejected, key: 'rejected' },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            className={`card-swiss p-4 text-left transition ${
              statusFilter === s.key ? 'ring-2 ring-[#E60000]' : 'hover:bg-[#F7F8FA]'
            }`}
            data-testid={`filter-${s.key}-btn`}
          >
            <p className="text-xs uppercase tracking-wider text-[#8A94A6]">{s.label}</p>
            <p className="text-2xl font-bold text-[#111418]">{s.value ?? 0}</p>
          </button>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Funnel weight="bold" size={16} className="text-[#8A94A6]" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="input-swiss w-[220px]" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="new">Neu</SelectItem>
            <SelectItem value="reviewed">Gesichtet</SelectItem>
            <SelectItem value="converted">Umgewandelt</SelectItem>
            <SelectItem value="rejected">Abgelehnt</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="card-swiss">
        {loading ? (
          <p className="p-8 text-center text-[#8A94A6]">Lade…</p>
        ) : inquiries.length === 0 ? (
          <div className="p-12 text-center">
            <ChatText weight="duotone" size={40} className="mx-auto text-[#8A94A6] mb-2" />
            <p className="text-[#525866] font-medium">Keine Anfragen</p>
            <p className="text-sm text-[#8A94A6] mt-1">
              Sobald Anfragen über die Homepage eingehen, erscheinen sie hier.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-[#E2E4E9] text-xs uppercase text-[#8A94A6]">
              <tr>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">Kunde</th>
                <th className="text-left px-4 py-3">Kontakt</th>
                <th className="text-left px-4 py-3">Fahrzeug / Zeitraum</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inq) => {
                const b = STATUS_BADGES[inq.status] || STATUS_BADGES.new;
                return (
                  <tr key={inq.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F8FA]" data-testid={`inquiry-row-${inq.id}`}>
                    <td className="px-4 py-3 text-sm">
                      {new Date(inq.created_at).toLocaleDateString('de-DE')}
                      <br />
                      <span className="text-xs text-[#8A94A6]">
                        {new Date(inq.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {inq.first_name} {inq.last_name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <a href={`mailto:${inq.email}`} className="text-[#E60000] hover:underline">{inq.email}</a>
                      {inq.phone && <div className="text-xs text-[#8A94A6]">{inq.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {inq.vehicle_preference && <div>{inq.vehicle_preference}</div>}
                      {inq.start_date && (
                        <div className="text-xs text-[#8A94A6]">
                          {new Date(inq.start_date).toLocaleDateString('de-DE')} – {inq.end_date ? new Date(inq.end_date).toLocaleDateString('de-DE') : '?'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${b.cls}`}>{b.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(inq)} title="Details" data-testid={`view-inquiry-${inq.id}`}>
                        <Eye weight="bold" size={16} />
                      </Button>
                      {inq.status !== 'converted' && (
                        <Button variant="ghost" size="sm" onClick={() => handleConvert(inq)} title="Zu Kunde" data-testid={`convert-inquiry-${inq.id}`}>
                          <ArrowRight weight="bold" size={16} className="text-[#009951]" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(inq.id)} title="Löschen">
                        <Trash weight="bold" size={16} className="text-[#E60000]" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anfrage von {selected?.first_name} {selected?.last_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2">
                <span className={`badge ${STATUS_BADGES[selected.status]?.cls || 'badge-neutral'}`}>
                  {STATUS_BADGES[selected.status]?.label || selected.status}
                </span>
                <span className="text-xs text-[#8A94A6]">
                  Eingegangen: {new Date(selected.created_at).toLocaleString('de-DE')}
                </span>
                {selected.source && <span className="text-xs text-[#8A94A6]">· Quelle: {selected.source}</span>}
              </div>
              <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm space-y-2">
                <div className="flex items-center gap-2"><User size={14} className="text-[#8A94A6]" /> {selected.first_name} {selected.last_name}</div>
                <div className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-[#8A94A6]" /> <a href={`mailto:${selected.email}`} className="text-[#E60000] hover:underline">{selected.email}</a></div>
                {selected.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-[#8A94A6]" /> {selected.phone}</div>}
                {selected.vehicle_preference && <div className="flex items-center gap-2"><Car size={14} className="text-[#8A94A6]" /> {selected.vehicle_preference}</div>}
                {(selected.start_date || selected.end_date) && (
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-[#8A94A6]" />
                    {selected.start_date ? new Date(selected.start_date).toLocaleDateString('de-DE') : '?'} – {selected.end_date ? new Date(selected.end_date).toLocaleDateString('de-DE') : '?'}
                  </div>
                )}
              </div>
              {selected.message && (
                <div>
                  <Label className="overline">Erste Nachricht</Label>
                  <p className="mt-1 p-3 bg-white border border-[#E2E4E9] rounded-sm whitespace-pre-wrap text-[#111418]">
                    {selected.message}
                  </p>
                </div>
              )}

              {/* === Conversation Thread === */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="overline">Verlauf ({messages.length})</Label>
                  {loadingMessages && <span className="text-xs text-[#8A94A6]">Lade…</span>}
                </div>
                {messages.length === 0 && !loadingMessages ? (
                  <p className="text-xs text-[#8A94A6] mt-1">
                    Noch keine E-Mail-Nachrichten in diesem Thread.
                  </p>
                ) : (
                  <div className="mt-2 space-y-2 max-h-[360px] overflow-y-auto pr-1">
                    {messages.map((m) => {
                      const isOut = m.direction === 'out';
                      return (
                        <div
                          key={m.id}
                          className={`border rounded-sm p-3 ${
                            isOut
                              ? 'bg-[#EAF4FF] border-[#B7D4FF] ml-6'
                              : 'bg-white border-[#E2E4E9] mr-6'
                          }`}
                          data-testid={`msg-${m.id}`}
                        >
                          <div className="flex items-center justify-between text-xs text-[#525866] mb-1">
                            <span className="font-medium">
                              {isOut ? (
                                <><ArrowUUpLeft size={12} className="inline mr-1" />Antwort an {m.to_email}</>
                              ) : (
                                <><EnvelopeSimple size={12} className="inline mr-1" />Von {m.from_name || m.from_email}</>
                              )}
                            </span>
                            <span className="text-[#8A94A6]">
                              {new Date(m.sent_at || m.created_at).toLocaleString('de-DE')}
                            </span>
                          </div>
                          {m.subject && (
                            <p className="font-semibold text-[#111418] mb-1 truncate" title={m.subject}>{m.subject}</p>
                          )}
                          <p className="whitespace-pre-wrap text-sm text-[#111418]">{m.body_plain}</p>
                          {(m.attachments || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {m.attachments.map((a) => (
                                <a
                                  key={a.id}
                                  href={inquiryApi.attachmentUrl(a.id)}
                                  target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#E2E4E9] rounded-sm text-xs hover:bg-[#F7F8FA]"
                                  data-testid={`attachment-${a.id}`}
                                >
                                  <DownloadSimple size={12} />
                                  {a.filename} <span className="text-[#8A94A6]">({Math.ceil((a.size || 0) / 1024)} KB)</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* === Reply Composer === */}
              <div className="border-t border-[#E2E4E9] pt-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="overline mb-0">Antwort senden an {selected.email}</Label>
                  {templates.length > 0 && (
                    <Select value={selectedTpl} onValueChange={applyTemplate}>
                      <SelectTrigger className="w-[220px] input-swiss h-8 text-xs" data-testid="apply-template-select">
                        <SelectValue placeholder="Vorlage wählen…" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`apply-tpl-${t.id}`}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <Input
                  placeholder="Betreff (optional — übernimmt sonst 'Re: Original')"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  data-testid="reply-subject-input"
                />
                <textarea
                  className="input-swiss w-full px-3 py-2 min-h-[140px]"
                  placeholder="Hallo, vielen Dank für Ihre Anfrage…"
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  data-testid="reply-body-input"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-dashed border-[#8A94A6] rounded-sm cursor-pointer text-xs hover:bg-[#F7F8FA]" data-testid="reply-attach-btn">
                    <Paperclip size={14} />
                    Anhang
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => setReplyFiles(Array.from(e.target.files || []))}
                    />
                  </label>
                  {replyFiles.length > 0 && (
                    <span className="text-xs text-[#525866]">
                      {replyFiles.length} Datei(en): {replyFiles.map(f => f.name).join(', ')}
                    </span>
                  )}
                  {replyBody.trim() && (
                    <SaveAsTemplateButton
                      subject={replySubject}
                      body={replyBody}
                      onSaved={() => emailTemplateApi.list().then((r) => setTemplates(r.data || []))}
                    />
                  )}
                  <Button
                    onClick={handleSendReply}
                    disabled={!replyBody.trim() || sendingReply}
                    className="btn-primary ml-auto"
                    data-testid="send-reply-btn"
                  >
                    <PaperPlaneRight weight="bold" size={14} className="mr-1" />
                    {sendingReply ? 'Sende…' : 'Antwort senden'}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {selected?.status !== 'reviewed' && selected?.status !== 'converted' && (
              <Button variant="outline" onClick={() => markStatus(selected.id, 'reviewed')}>
                Als gesichtet markieren
              </Button>
            )}
            {selected?.status !== 'rejected' && selected?.status !== 'converted' && (
              <Button variant="outline" onClick={() => markStatus(selected.id, 'rejected')} className="text-[#E60000] border-[#E60000]">
                <XCircle weight="bold" size={14} className="mr-1" /> Ablehnen
              </Button>
            )}
            {selected?.status !== 'converted' && (
              <Button className="btn-primary" onClick={() => handleConvert(selected)}>
                <CheckCircle weight="bold" size={14} className="mr-1" /> Zu Kunde umwandeln
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Inquiry Dialog */}
      <ManualInquiryDialog
        open={showManualDialog}
        onClose={() => setShowManualDialog(false)}
        onCreated={() => load()}
      />
    </div>
  );
};

export default Inquiries;
